import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { z } from "zod";
import { ChevronDown, ChevronLeft, Scale as ScaleIcon } from "lucide-react";

import { useCandidates } from "@/application/use-cases/candidates/use-candidates.queries";
import { useAllCandidateChoices } from "@/application/use-cases/candidates/use-candidate-choices.queries";
import { useEvaluationCriteria } from "@/application/use-cases/products/use-criteria.queries";
import { useProducts } from "@/application/use-cases/products/use-products.queries";
import { useAllProductScores } from "@/application/use-cases/products/use-scores.queries";
import { useDefaultWeightsPreset } from "@/application/use-cases/products/use-weights.queries";
import { useAllFeatureProducts } from "@/application/use-cases/products/use-feature-products.queries";
import { useFeaturesList } from "@/application/use-cases/features/use-features.queries";
import { useAllVerdicts } from "@/application/use-cases/feature-roadmap-verdicts/use-verdicts.queries";
import {
  computeCandidateAggregate,
  type CandidateAggregateResult,
} from "@/application/use-cases/candidates/compute-candidate-aggregates";
import { useComponentCriticality } from "@/application/use-cases/candidates/use-component-criticality.queries";
import { PageExplainer } from "@/presentation/components/shell/page-explainer";
import { RouteHero } from "@/presentation/components/shell/route-hero";
import { ActivePresetSelector } from "@/presentation/components/products/active-preset-selector";
// RouteHero stays for the picker fallback below.
import type {
  CandidateRow,
  EvaluationCriterionRow,
  ProductScoreRow,
} from "@/infrastructure/supabase/database.types";

const searchSchema = z.object({
  a: z.string().optional(),
  b: z.string().optional(),
});

export const Route = createFileRoute("/_app/candidates/compare-summary")({
  validateSearch: searchSchema,
  component: CompareSummaryPage,
});

// Side-color tokens — green for A, blue for B (mirrors candidate-card provenance palette).
const A_COLOR = "oklch(0.55 0.16 145)";
const A_SOFT = "oklch(0.96 0.05 145)";
const A_INK = "oklch(0.32 0.13 150)";
const B_COLOR = "oklch(0.55 0.17 260)";
const B_SOFT = "oklch(0.96 0.05 260)";
const B_INK = "oklch(0.32 0.13 270)";
const GOOD = "oklch(0.52 0.15 150)";
const GOOD_BG = "oklch(0.95 0.06 150)";
const MID = "oklch(0.62 0.13 75)";
const MID_BG = "oklch(0.96 0.07 75)";
const BAD = "oklch(0.58 0.18 25)";
const BAD_BG = "oklch(0.96 0.04 25)";

// Static "you get / you give up" bullets curated for the open-source vs shelf comparison.
// Generalising to any A/B pair is a follow-up — for the stakeholder demo this is the focused content.
const TRADEOFF_TEMPLATE: Record<
  "a" | "b",
  {
    get: { title: string; body: string }[];
    give: { title: string; body: string }[];
  }
> = {
  a: {
    get: [
      {
        title: "Lower TCO at scale",
        body: "No per-seat licence; cost tracks infrastructure, not headcount.",
      },
      {
        title: "Extensibility headroom",
        body: "Build integration & product in parallel — no vendor PS queue on the critical path.",
      },
      {
        title: "Data ownership & exit control",
        body: "Self-hosted, portable, no lock-in accrues.",
      },
      {
        title: "Better admin self-serve",
        body: "Slightly stronger non-technical operability.",
      },
    ],
    give: [
      {
        title: "Out-of-the-box Israeli fit",
        body: "RTL, ILS, IL processors become build work.",
      },
      {
        title: "Local connector ecosystem",
        body: "Every IL integration is custom; onboarding cost stays high.",
      },
    ],
  },
  b: {
    get: [
      {
        title: "Israeli-native fit",
        body: "RTL, ILS, local rails baked in; faster to be Israel-ready.",
      },
      {
        title: "Richer IL ecosystem",
        body: "Pre-built connectors; onboarding a B2B customer is closer to config.",
      },
    ],
    give: [
      {
        title: "Cost-efficiency at scale",
        body: "Licence & per-tenant fees compress margin as you grow.",
      },
      {
        title: "Extensibility headroom",
        body: "More is gated behind the vendor's roadmap / PS queue.",
      },
      {
        title: "Some data-portability & exit leverage",
        body: "Lock-in accrues over the deployment.",
      },
    ],
  },
};

function CompareSummaryPage() {
  const search = Route.useSearch();
  const candidatesQ = useCandidates();
  const choicesQ = useAllCandidateChoices();
  const productsQ = useProducts();
  const scoresQ = useAllProductScores();
  const criteriaQ = useEvaluationCriteria();
  const presetQ = useDefaultWeightsPreset();
  const featuresQ = useFeaturesList();
  const fpQ = useAllFeatureProducts();
  const verdictsQ = useAllVerdicts();
  const criticalityQ = useComponentCriticality();

  const candidates = candidatesQ.data ?? [];
  const allChoices = choicesQ.data ?? [];
  const products = productsQ.data ?? [];
  const productScores = scoresQ.data ?? [];
  const criteria = criteriaQ.data ?? [];
  const weights = presetQ.data?.weights ?? {};
  const features = featuresQ.data ?? [];
  const featureProducts = fpQ.data ?? [];
  const verdicts = verdictsQ.data ?? [];
  const criticalityOverrides = criticalityQ.data ?? {};

  const a = candidates.find((c) => c.slug === search.a) ?? null;
  const b = candidates.find((c) => c.slug === search.b) ?? null;

  const choicesA = a ? allChoices.filter((c) => c.candidate_id === a.id) : [];
  const choicesB = b ? allChoices.filter((c) => c.candidate_id === b.id) : [];

  const aggInputsA = {
    choices: choicesA,
    products,
    productScores,
    criteria,
    features,
    featureProducts,
    verdicts,
    weights,
    criticalityOverrides,
  };
  const aggInputsB = { ...aggInputsA, choices: choicesB };

  const aggA = useMemo(
    () => (a ? computeCandidateAggregate(aggInputsA) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      a,
      choicesA,
      products,
      productScores,
      criteria,
      features,
      featureProducts,
      verdicts,
      weights,
      criticalityOverrides,
    ]
  );
  const aggB = useMemo(
    () => (b ? computeCandidateAggregate(aggInputsB) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      b,
      choicesB,
      products,
      productScores,
      criteria,
      features,
      featureProducts,
      verdicts,
      weights,
      criticalityOverrides,
    ]
  );

  const rows = useMemo(() => {
    if (!a || !b || !aggA || !aggB) return [];
    return buildCriterionRows({
      criteria,
      productScores,
      choicesA,
      choicesB,
      weights,
      aggA,
      aggB,
    });
  }, [a, b, aggA, aggB, criteria, productScores, choicesA, choicesB, weights]);

  if (!a || !b) {
    return (
      <ChooseTwo candidates={candidates} currentA={search.a ?? null} currentB={search.b ?? null} />
    );
  }

  return (
    <div className="animate-page-enter mx-auto flex w-full max-w-6xl flex-col gap-8 p-6 lg:gap-10 lg:p-10">
      <div className="self-end print:hidden">
        <PageExplainer pageKey="compare-summary" />
      </div>
      <div className="flex items-center gap-2 text-[12px] print:hidden">
        <Link
          to="/candidates"
          className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 transition-colors"
        >
          <ChevronLeft className="size-3" />
          All candidates
        </Link>
      </div>

      <CompareSummaryHeader
        a={a}
        b={b}
        aCompositeText={aggA?.composite?.toFixed(2) ?? "—"}
        bCompositeText={aggB?.composite?.toFixed(2) ?? "—"}
        edges={rows.filter((r) => r.edge !== "tie").length}
        wash={rows.filter((r) => r.edge === "tie").length}
      />

      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <ActivePresetSelector />
        <span className="text-muted-foreground text-[10.5px]">
          Switch the lens — every score in the matrix and every edge recomputes.
        </span>
      </div>

      <ArchHeads a={a} aggA={aggA} b={b} aggB={aggB} />

      <GetGiveUp a={a} b={b} rows={rows} />

      <CollapsibleMatrix rows={rows} />
    </div>
  );
}

function CompareSummaryHeader({
  a,
  b,
  aCompositeText,
  bCompositeText,
  edges,
  wash,
}: {
  a: CandidateRow;
  b: CandidateRow;
  aCompositeText: string;
  bCompositeText: string;
  edges: number;
  wash: number;
}) {
  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span
          className="inline-flex h-6 w-6 items-center justify-center rounded-full"
          style={{ background: "oklch(0.94 0.04 200)", color: "oklch(0.4 0.13 200)" }}
        >
          <ScaleIcon className="size-3.5" />
        </span>
        <span className="font-data text-foreground/55 text-[10.5px] tracking-[0.14em] uppercase">
          Summary compare · stakeholder view
        </span>
      </div>
      <h1 className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-[22px] leading-tight font-semibold tracking-tight">
        <span className="inline-flex items-baseline gap-1.5">
          <span style={{ color: A_COLOR }}>{a.name}</span>
          <span className="font-data text-[14px] tabular-nums" style={{ color: A_COLOR }}>
            {aCompositeText}
          </span>
        </span>
        <span className="text-foreground/30 text-[14px] font-normal">↔</span>
        <span className="inline-flex items-baseline gap-1.5">
          <span style={{ color: B_COLOR }}>{b.name}</span>
          <span className="font-data text-[14px] tabular-nums" style={{ color: B_COLOR }}>
            {bCompositeText}
          </span>
        </span>
      </h1>
      <p className="text-muted-foreground text-[12.5px]">
        What you get and what you give up — at a glance.{" "}
        <span className="font-data text-foreground/50 text-[10.5px] tracking-wider uppercase">
          {edges} edge{edges === 1 ? "" : "s"} · {wash} wash
        </span>
      </p>
    </section>
  );
}

function ArchHeads({
  a,
  aggA,
  b,
  aggB,
}: {
  a: CandidateRow;
  aggA: CandidateAggregateResult | null;
  b: CandidateRow;
  aggB: CandidateAggregateResult | null;
}) {
  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
      <ArchHead
        side="a"
        title={a.name}
        slug={a.slug}
        composite={aggA?.composite ?? null}
        productCount={
          aggA ? new Set(aggA.componentAggregates.map((c) => c.productId).filter(Boolean)).size : 0
        }
        coverage={aggA?.coverage.b2c?.totalRatio ?? null}
      />
      <ArchHead
        side="b"
        title={b.name}
        slug={b.slug}
        composite={aggB?.composite ?? null}
        productCount={
          aggB ? new Set(aggB.componentAggregates.map((c) => c.productId).filter(Boolean)).size : 0
        }
        coverage={aggB?.coverage.b2c?.totalRatio ?? null}
      />
    </div>
  );
}

function ArchHead({
  side,
  title,
  slug,
  composite,
  productCount,
  coverage,
}: {
  side: "a" | "b";
  title: string;
  slug: string;
  composite: number | null;
  productCount: number;
  coverage: number | null;
}) {
  const color = side === "a" ? A_COLOR : B_COLOR;
  const soft = side === "a" ? A_SOFT : B_SOFT;
  const ink = side === "a" ? A_INK : B_INK;
  return (
    <div
      className="flex flex-col gap-1 rounded-2xl border p-4 print:break-inside-avoid"
      style={{
        borderColor: "oklch(0.91 0.008 240)",
        background: `linear-gradient(180deg, ${soft}, white)`,
      }}
    >
      <span className="font-data text-[10px] tracking-[0.14em] uppercase" style={{ color }}>
        Arch {side.toUpperCase()}
      </span>
      <div className="text-[16px] font-semibold" style={{ color: ink }}>
        {title}
      </div>
      <div className="font-data text-foreground/55 mt-1 text-[10.5px] tracking-tight">
        composite{" "}
        <span className="font-semibold" style={{ color: ink }}>
          {composite !== null ? composite.toFixed(2) : "—"}
        </span>{" "}
        · {productCount} distinct products · B2C coverage{" "}
        {coverage !== null ? `${Math.round(coverage * 100)}%` : "—"}
      </div>
      <Link
        to="/candidates/$slug"
        params={{ slug }}
        className="text-foreground/50 hover:text-foreground mt-1 inline-flex w-fit items-center text-[10px] tracking-wider uppercase transition-colors"
      >
        View candidate →
      </Link>
    </div>
  );
}

function GetGiveUp({ a, b, rows }: { a: CandidateRow; b: CandidateRow; rows: CriterionRow[] }) {
  // Wash items = ties for the bottom strip
  const wash = rows.filter((r) => r.edge === "tie").map((r) => r.label);
  return (
    <section className="flex flex-col gap-3">
      <h2 className="eyebrow">What you get · what you give up</h2>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <GgColumn side="a" candidateName={a.name} />
        <GgColumn side="b" candidateName={b.name} />
      </div>
      {wash.length > 0 ? (
        <div
          className="rounded-xl border border-dashed px-4 py-3 text-[12px] leading-relaxed"
          style={{ borderColor: "oklch(0.86 0.01 240)", color: "oklch(0.42 0.015 250)" }}
        >
          <span className="text-foreground font-semibold">A wash either way</span>{" "}
          <span className="text-foreground/50">(within 0.3, doesn't move the decision):</span>{" "}
          {wash.join(" · ")}.
        </div>
      ) : null}
    </section>
  );
}

function GgColumn({ side, candidateName }: { side: "a" | "b"; candidateName: string }) {
  const t = TRADEOFF_TEMPLATE[side];
  const soft = side === "a" ? A_SOFT : B_SOFT;
  const ink = side === "a" ? A_INK : B_INK;
  return (
    <div
      className="overflow-hidden rounded-2xl border print:break-inside-avoid"
      style={{ borderColor: "oklch(0.91 0.008 240)" }}
    >
      <div className="px-4 py-3 text-[14px] font-semibold" style={{ background: soft, color: ink }}>
        If you choose {side.toUpperCase()} · {candidateName}
      </div>
      <div className="border-t" style={{ borderColor: "oklch(0.94 0.006 240)" }}>
        <GgSection tone="good" title="You get" items={t.get} />
      </div>
      <div className="border-t" style={{ borderColor: "oklch(0.94 0.006 240)" }}>
        <GgSection tone="bad" title="You give up" items={t.give} />
      </div>
    </div>
  );
}

function GgSection({
  tone,
  title,
  items,
}: {
  tone: "good" | "bad";
  title: string;
  items: { title: string; body: string }[];
}) {
  const color = tone === "good" ? GOOD : BAD;
  const symbol = tone === "good" ? "✓" : "✗";
  return (
    <div className="px-4 py-3">
      <h5 className="font-data m-0 mb-2 text-[10px] tracking-[0.12em] uppercase" style={{ color }}>
        {symbol} {title}
      </h5>
      <ul className="flex flex-col gap-1.5">
        {items.map((it) => (
          <li key={it.title} className="flex items-start gap-2 text-[12.5px]">
            <span className="mt-0.5 font-bold" style={{ color }}>
              {symbol}
            </span>
            <span>
              <span className="font-semibold">{it.title}</span> — {it.body}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CollapsibleMatrix({ rows }: { rows: CriterionRow[] }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="flex flex-col gap-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="hover:bg-muted/30 flex items-baseline justify-between gap-3 rounded-md border-b border-dashed border-[oklch(0.91_0.008_240)] py-2 transition-colors print:hidden"
      >
        <div className="flex items-baseline gap-2">
          <ChevronDown
            className="text-foreground/55 size-3 transition-transform"
            style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
          />
          <h2 className="eyebrow m-0">Verdict matrix — ✓ / ~ / ✗ per criterion</h2>
        </div>
        <span className="font-data text-foreground/55 text-[10px] tracking-wider uppercase">
          {open ? "hide" : "click to expand"}
        </span>
      </button>
      {open ? (
        <div className="glow-card p-5">
          <p className="text-muted-foreground mb-4 text-[12px]">
            Sorted by weight (importance). Symbol = absolute posture; the arrow column marks which
            architecture has the edge (Δ ≥ 0.3). Winning cell gets a coloured rail.
          </p>
          <MatrixTable rows={rows} />
          <MatrixLegend />
          <p className="text-muted-foreground mt-3 text-[10.5px] italic">
            Multi-tenant isolation omitted (weight 0%, single-operator engagement).
          </p>
        </div>
      ) : null}
    </section>
  );
}

function MatrixTable({ rows }: { rows: CriterionRow[] }) {
  return (
    <table className="w-full border-collapse">
      <thead>
        <tr>
          <th className="font-data text-foreground/55 w-[40%] border-b border-[oklch(0.91_0.008_240)] py-2 pr-2 text-left text-[10px] tracking-wider uppercase">
            Criterion
          </th>
          <th
            className="font-data w-[22%] border-b border-[oklch(0.91_0.008_240)] py-2 text-center text-[10px] tracking-wider uppercase"
            style={{ color: A_COLOR }}
          >
            A
          </th>
          <th className="font-data text-foreground/55 w-[8%] border-b border-[oklch(0.91_0.008_240)] py-2 text-center text-[10px] tracking-wider uppercase">
            edge
          </th>
          <th
            className="font-data w-[22%] border-b border-[oklch(0.91_0.008_240)] py-2 text-center text-[10px] tracking-wider uppercase"
            style={{ color: B_COLOR }}
          >
            B
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.slug} className="hover:bg-muted/30 print:break-inside-avoid">
            <td className="border-b border-[oklch(0.95_0.005_240)] py-2 pr-2 text-[12.5px]">
              <span className="font-medium">{r.label}</span>
              <span className="font-data text-foreground/45 ml-2 text-[10px]">
                {Math.round((r.weight ?? 0) * 100)}%
              </span>
            </td>
            <CellTd score={r.scoreA} winner={r.edge === "a"} side="a" coverage={r.coverage} />
            <td className="border-b border-[oklch(0.95_0.005_240)] py-2 text-center">
              <EdgeMarker edge={r.edge} />
            </td>
            <CellTd score={r.scoreB} winner={r.edge === "b"} side="b" coverage={r.coverage} />
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CellTd({
  score,
  winner,
  side,
  coverage,
}: {
  score: number | null;
  winner: boolean;
  side: "a" | "b";
  coverage: boolean;
}) {
  const color = side === "a" ? A_COLOR : B_COLOR;
  return (
    <td
      className="border-b border-[oklch(0.95_0.005_240)] py-2 text-center"
      style={
        winner
          ? {
              boxShadow: side === "a" ? `inset 3px 0 0 ${color}` : `inset -3px 0 0 ${color}`,
            }
          : undefined
      }
    >
      <PostureSymbol score={score} />
      <span className="font-data text-foreground/55 mt-1 block text-[10px]">
        {score === null ? "—" : coverage ? `${Math.round(score * 20)}%` : score.toFixed(1)}
      </span>
    </td>
  );
}

function PostureSymbol({ score }: { score: number | null }) {
  if (score === null) {
    return (
      <span
        className="inline-flex h-6 w-6 items-center justify-center rounded-md text-[14px] font-bold"
        style={{ background: "oklch(0.94 0.005 240)", color: "oklch(0.7 0.01 240)" }}
      >
        ·
      </span>
    );
  }
  const tone: "good" | "mid" | "bad" = score >= 3.7 ? "good" : score >= 3.0 ? "mid" : "bad";
  const sym = tone === "good" ? "✓" : tone === "mid" ? "~" : "✗";
  const bg = tone === "good" ? GOOD_BG : tone === "mid" ? MID_BG : BAD_BG;
  const color = tone === "good" ? GOOD : tone === "mid" ? MID : BAD;
  return (
    <span
      className="inline-flex h-6 w-6 items-center justify-center rounded-md text-[14px] font-bold"
      style={{ background: bg, color }}
    >
      {sym}
    </span>
  );
}

function EdgeMarker({ edge }: { edge: "a" | "b" | "tie" }) {
  if (edge === "tie") {
    return <span className="text-foreground/35 text-[13px] font-bold">=</span>;
  }
  return (
    <span className="text-[13px] font-bold" style={{ color: edge === "a" ? A_COLOR : B_COLOR }}>
      {edge === "a" ? "◀" : "▶"}
    </span>
  );
}

function MatrixLegend() {
  return (
    <div className="text-muted-foreground mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px]">
      <span className="inline-flex items-center gap-1.5">
        <PostureSymbol score={4.5} /> strong (≥3.7)
      </span>
      <span className="inline-flex items-center gap-1.5">
        <PostureSymbol score={3.3} /> mixed (3.0–3.6)
      </span>
      <span className="inline-flex items-center gap-1.5">
        <PostureSymbol score={2.5} /> weak (&lt;3.0)
      </span>
      <span style={{ color: A_COLOR }}>◀ A edge</span>
      <span style={{ color: B_COLOR }}>B edge ▶</span>
      <span>= within 0.3</span>
    </div>
  );
}

// ── data: per-criterion rows ─────────────────────────────────────────────────

interface CriterionRow {
  slug: string;
  label: string;
  weight: number;
  scoreA: number | null;
  scoreB: number | null;
  edge: "a" | "b" | "tie";
  coverage: boolean;
}

function buildCriterionRows({
  criteria,
  productScores,
  choicesA,
  choicesB,
  weights,
  aggA,
  aggB,
}: {
  criteria: EvaluationCriterionRow[];
  productScores: ProductScoreRow[];
  choicesA: { product_id: string | null }[];
  choicesB: { product_id: string | null }[];
  weights: Record<string, number>;
  aggA: CandidateAggregateResult;
  aggB: CandidateAggregateResult;
}): CriterionRow[] {
  const productIdsA = new Set(choicesA.map((c) => c.product_id).filter(Boolean) as string[]);
  const productIdsB = new Set(choicesB.map((c) => c.product_id).filter(Boolean) as string[]);

  const meanScoreForCriterion = (criterionId: string, productIds: Set<string>): number | null => {
    const xs = productScores
      .filter(
        (s) =>
          s.criterion_id === criterionId &&
          productIds.has(s.product_id) &&
          s.score !== null &&
          !s.dealbreaker_fail
      )
      .map((s) => s.score as number);
    if (xs.length === 0) return null;
    return xs.reduce((a, x) => a + x, 0) / xs.length;
  };

  const rows: CriterionRow[] = [];
  for (const c of criteria) {
    const w = weights[c.slug] ?? c.weight_default ?? 0;
    if (w === 0) continue; // skip zero-weighted criteria (e.g. multi-tenant-isolation)

    let scoreA: number | null;
    let scoreB: number | null;
    let coverage = false;
    if (c.derivation_kind === "derived-coverage") {
      coverage = true;
      const map: Record<string, keyof CandidateAggregateResult["coverage"] | null> = {
        "b2c-feature-coverage": "b2c",
        "non-mvp-b2b-feature-coverage": "b2bNonMvp",
        "all-in-one-coverage": null,
      };
      const journey = map[c.slug] ?? null;
      if (journey) {
        scoreA =
          (aggA.coverage[journey]?.totalRatio ?? null) !== null
            ? aggA.coverage[journey]!.totalRatio * 5
            : null;
        scoreB =
          (aggB.coverage[journey]?.totalRatio ?? null) !== null
            ? aggB.coverage[journey]!.totalRatio * 5
            : null;
      } else {
        // all-in-one: average of the three journeys
        const avg = (agg: CandidateAggregateResult) => {
          const vals = [agg.coverage.b2c, agg.coverage.b2bMvp, agg.coverage.b2bNonMvp]
            .map((c) => c?.totalRatio)
            .filter((v): v is number => v !== null && v !== undefined);
          if (vals.length === 0) return null;
          return (vals.reduce((s, v) => s + v, 0) / vals.length) * 5;
        };
        scoreA = avg(aggA);
        scoreB = avg(aggB);
      }
    } else {
      scoreA = meanScoreForCriterion(c.id, productIdsA);
      scoreB = meanScoreForCriterion(c.id, productIdsB);
    }

    let edge: "a" | "b" | "tie" = "tie";
    if (scoreA !== null && scoreB !== null) {
      const d = scoreA - scoreB;
      if (d >= 0.3) edge = "a";
      else if (d <= -0.3) edge = "b";
    }

    rows.push({
      slug: c.slug,
      label: c.name,
      weight: w,
      scoreA,
      scoreB,
      edge,
      coverage,
    });
  }
  // Sort by weight desc
  rows.sort((a, b) => b.weight - a.weight);
  return rows;
}

// ── ChooseTwo picker (mirrors compare.tsx pattern) ───────────────────────────

function ChooseTwo({
  candidates,
  currentA,
  currentB,
}: {
  candidates: CandidateRow[];
  currentA: string | null;
  currentB: string | null;
}) {
  const navigate = Route.useNavigate();
  const [a, setA] = useState<string | null>(currentA);
  const [b, setB] = useState<string | null>(currentB);
  const canGo = a !== null && b !== null && a !== b;
  return (
    <div className="animate-page-enter mx-auto flex w-full max-w-3xl flex-col gap-6 p-6 lg:p-10">
      <div className="flex items-center gap-2 text-[12px]">
        <Link
          to="/candidates"
          className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 transition-colors"
        >
          <ChevronLeft className="size-3" />
          All candidates
        </Link>
      </div>
      <RouteHero
        eyebrow="Summary compare · pick two"
        icon={ScaleIcon}
        numeral="A B"
        numeralTone="cyan"
        numeralSuffix=""
        title="Compare two architectures"
        summary="Pick the two candidates you want to walk a stakeholder through. The page renders the get / give-up trade-off above the verdict matrix."
        meta={[]}
      />
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <PickerColumn
          side="a"
          label="Architecture A"
          value={a}
          onChange={setA}
          candidates={candidates}
        />
        <PickerColumn
          side="b"
          label="Architecture B"
          value={b}
          onChange={setB}
          candidates={candidates}
        />
      </div>
      <button
        type="button"
        disabled={!canGo}
        onClick={() => {
          if (!a || !b) return;
          void navigate({ search: { a, b } });
        }}
        className="hover:bg-foreground/5 inline-flex h-10 w-fit items-center gap-1.5 self-start rounded-md border border-[oklch(0.91_0.008_240)] px-4 text-[12.5px] font-medium transition-colors disabled:opacity-40"
      >
        Compare →
      </button>
    </div>
  );
}

function PickerColumn({
  side,
  label,
  value,
  onChange,
  candidates,
}: {
  side: "a" | "b";
  label: string;
  value: string | null;
  onChange: (slug: string | null) => void;
  candidates: CandidateRow[];
}) {
  const color = side === "a" ? A_COLOR : B_COLOR;
  return (
    <div className="glow-card flex flex-col gap-2 p-4">
      <span className="font-data text-[10px] tracking-[0.14em] uppercase" style={{ color }}>
        {label}
      </span>
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
        className="border-input bg-background h-9 rounded-md border px-3 text-[12.5px]"
      >
        <option value="">— pick a candidate —</option>
        {candidates.map((c) => (
          <option key={c.id} value={c.slug}>
            {c.name}
          </option>
        ))}
      </select>
    </div>
  );
}
